import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import {
  getCartItemMinimumQuantity,
  getCartItemQuantityError,
  MAX_CART_ITEM_QUANTITY,
  MAX_CART_LINE_ITEMS,
} from '../cart-pricing.util';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../utils';
import { AuthService, User } from './auth.service';

interface StoredCart {
  items: any[];
  deletedItems: Record<string, number>;
  mergedGuestIds: string[];
}

@Injectable({ providedIn: 'root' })
export class CartService implements OnDestroy {
  private readonly guestCartKey = 'cart:guest';
  private cartStorageKey: string;
  private cartSubject: BehaviorSubject<any[]>;
  private readonly cartNoticeSubject = new BehaviorSubject<string | null>(null);
  private readonly authSubscription: Subscription;
  private deletedItemTimestamps = new Map<string, number>();
  private mergedGuestIds = new Set<string>();
  private lastCartUpdateTimestamp = 0;
  private readonly cartClientId =
    `cart-client-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  private readonly storageListener = (event: StorageEvent): void => {
    if (event.key !== this.cartStorageKey && event.key !== null) return;

    if (event.key === null || event.newValue === null) {
      this.updateCartStorage([]);
      return;
    }

    const incoming = this.parseStoredCart(event.newValue);
    const localItems = this.cartSubject.getValue();
    const mergedDeletions = new Map(this.deletedItemTimestamps);
    for (const [id, timestamp] of Object.entries(incoming.deletedItems)) {
      mergedDeletions.set(
        id,
        Math.max(mergedDeletions.get(id) || 0, timestamp),
      );
    }
    this.deletedItemTimestamps = mergedDeletions;
    this.mergedGuestIds = new Set([
      ...this.mergedGuestIds,
      ...incoming.mergedGuestIds,
    ]);

    const itemsById = new Map<string, any>();
    for (const item of [...localItems, ...incoming.items]) {
      const id = String(item?.id);
      const existing = itemsById.get(id);
      const itemUpdatedAt = Number(item?._cartUpdatedAt || 0);
      const existingUpdatedAt = Number(existing?._cartUpdatedAt || 0);
      if (
        !existing ||
        itemUpdatedAt > existingUpdatedAt ||
        (itemUpdatedAt === existingUpdatedAt &&
          String(item?._cartUpdatedBy || '') >
            String(existing?._cartUpdatedBy || ''))
      ) {
        itemsById.set(id, item);
      }
    }
    for (const item of incoming.items) {
      this.lastCartUpdateTimestamp = Math.max(
        this.lastCartUpdateTimestamp,
        Number(item?._cartUpdatedAt || 0),
      );
    }
    const mergedItems = [...itemsById.values()].filter(
      (item) => {
        const deletedAt = this.deletedItemTimestamps.get(String(item?.id));
        return deletedAt === undefined ||
          Number(item?._cartUpdatedAt || 0) > deletedAt;
      },
    );
    const mergedDeletionSnapshot = Object.fromEntries(
      [...this.deletedItemTimestamps.entries()].sort(([left], [right]) =>
        left.localeCompare(right),
      ),
    );
    const incomingDeletions = Object.fromEntries(
      Object.entries(incoming.deletedItems).sort(([left], [right]) =>
        left.localeCompare(right),
      ),
    );
    const mergedGuestIds = [...this.mergedGuestIds].sort();
    const incomingGuestIds = [...incoming.mergedGuestIds].sort();
    const storageAlreadyMerged =
      mergedItems.length === incoming.items.length &&
      mergedItems.every((item) => {
        const incomingItem = incoming.items.find(
          (candidate) => String(candidate?.id) === String(item?.id),
        );
        return JSON.stringify(item) === JSON.stringify(incomingItem);
      }) &&
      JSON.stringify(mergedDeletionSnapshot) === JSON.stringify(incomingDeletions) &&
      JSON.stringify(mergedGuestIds) === JSON.stringify(incomingGuestIds);
    if (storageAlreadyMerged) {
      this.cartSubject.next(this.copyCartItems(incoming.items));
      return;
    }
    this.updateCartStorage(mergedItems);
  };

  constructor(private readonly authService: AuthService) {
    this.cartStorageKey = this.getCartStorageKey(this.authService.getCurrentUser());
    const rawStoredCart = getLocalStorageItem(this.cartStorageKey);
    const storedCart = this.readStoredCart(this.cartStorageKey);
    const invalidStoredShape = this.hasInvalidStoredCartShape(rawStoredCart);
    this.deletedItemTimestamps = new Map(Object.entries(storedCart.deletedItems));
    this.mergedGuestIds = new Set(storedCart.mergedGuestIds);
    const { cart: initialCart, changed } = this.ensureUniqueCartItemIds(
      storedCart.items,
    );
    this.cartSubject = new BehaviorSubject<any[]>(initialCart);
    if (changed || invalidStoredShape) this.updateCartStorage(initialCart);
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.storageListener);
    }
    this.authSubscription = this.authService.currentUser$
      .pipe(
        map((user) => this.getCartStorageKey(user)),
        distinctUntilChanged()
      )
      .subscribe((storageKey) => this.switchCart(storageKey));
  }

  private openCartDrawerSubject = new BehaviorSubject<boolean>(false);

  // Observable for opening cart drawer
  get openCartDrawer$(): Observable<boolean> {
    return this.openCartDrawerSubject.asObservable();
  }

  openCartDrawer(): void {
    this.openCartDrawerSubject.next(true);
  }

  // Observable that components can subscribe to
  get cart$(): Observable<any[]> {
    return this.cartSubject.asObservable().pipe(
      distinctUntilChanged(),
      map((cart) => this.copyCartItems(cart))
    );
  }

  get cartNotice$(): Observable<string | null> {
    return this.cartNoticeSubject.asObservable();
  }

  // Observable for cart count
  get cartCount$(): Observable<number> {
    return this.cart$.pipe(
      map((cart) => cart.reduce((total, item) => total + (Number(item.quantity) || 0), 0)),
      distinctUntilChanged()
    );
  }

  private updateCartStorage(cart: any[]): boolean {
    const currentItems = this.cartSubject.getValue();
    const currentById = new Map(
      currentItems.map((item) => [String(item?.id), item]),
    );
    const nextById = new Map(
      cart.map((item) => [String(item?.id), item]),
    );
    const timestamp = this.nextCartTimestamp();
    for (const [id, item] of currentById) {
      if (!nextById.has(id)) {
        this.deletedItemTimestamps.set(id, timestamp);
      }
    }

    const nextCart = this.copyCartItems(cart).map((item) => {
      const id = String(item?.id);
      const previous = currentById.get(id);
      const {
        _cartUpdatedAt: _previousTimestamp,
        _cartUpdatedBy: _previousClientId,
        ...previousData
      } = previous || {};
      const {
        _cartUpdatedAt: _nextTimestamp,
        _cartUpdatedBy: _nextClientId,
        ...nextData
      } = item;
      const changed = !previous || JSON.stringify(previousData) !== JSON.stringify(nextData);
      const updatedAt = changed
        ? timestamp
        : Number(item?._cartUpdatedAt || previous?._cartUpdatedAt || timestamp);
      const updatedBy = changed
        ? this.cartClientId
        : String(item?._cartUpdatedBy || previous?._cartUpdatedBy || this.cartClientId);
      this.deletedItemTimestamps.delete(id);
      return {
        ...item,
        _cartUpdatedAt: updatedAt,
        _cartUpdatedBy: updatedBy,
      };
    });
    try {
      setLocalStorageItem(this.cartStorageKey, {
        version: 1,
        items: nextCart,
        deletedItems: Object.fromEntries(this.deletedItemTimestamps),
        mergedGuestIds: [...this.mergedGuestIds],
      });
      this.cartSubject.next(nextCart);
      this.cartNoticeSubject.next(null);
      return true;
    } catch (error) {
      console.error('Could not save the shopping cart to browser storage.', error);
      this.cartSubject.next(nextCart);
      this.cartNoticeSubject.next(
        'Nie udało się zapisać koszyka na tym urządzeniu. Nie zamykaj tej karty podczas zakupów.',
      );
      return false;
    }
  }

  private nextCartTimestamp(): number {
    let latestTimestamp = this.lastCartUpdateTimestamp;
    for (const timestamp of this.deletedItemTimestamps.values()) {
      latestTimestamp = Math.max(latestTimestamp, Number(timestamp) || 0);
    }
    for (const item of this.cartSubject.getValue()) {
      latestTimestamp = Math.max(
        latestTimestamp,
        Number(item?._cartUpdatedAt) || 0,
      );
    }
    this.lastCartUpdateTimestamp = Math.max(Date.now(), latestTimestamp + 1);
    return this.lastCartUpdateTimestamp;
  }

  private readStoredCart(storageKey: string): StoredCart {
    const value = getLocalStorageItem(storageKey);
    if (Array.isArray(value)) {
      return { items: value, deletedItems: {}, mergedGuestIds: [] };
    }
    if (!value || typeof value !== 'object') {
      return { items: [], deletedItems: {}, mergedGuestIds: [] };
    }
    return {
      items: Array.isArray(value.items) ? value.items : [],
      deletedItems:
        value.deletedItems && typeof value.deletedItems === 'object'
          ? value.deletedItems
          : {},
      mergedGuestIds: Array.isArray(value.mergedGuestIds)
        ? value.mergedGuestIds.map(String)
        : [],
    };
  }

  private hasInvalidStoredCartShape(value: any): boolean {
    return value !== null &&
      !Array.isArray(value) &&
      (!value || typeof value !== 'object' || !Array.isArray(value.items));
  }

  private parseStoredCart(value: string): StoredCart {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return { items: parsed, deletedItems: {}, mergedGuestIds: [] };
      }
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Stored cart has an invalid shape.');
      }
      return {
        items: Array.isArray(parsed.items) ? parsed.items : [],
        deletedItems:
          parsed.deletedItems && typeof parsed.deletedItems === 'object'
            ? parsed.deletedItems
            : {},
        mergedGuestIds: Array.isArray(parsed.mergedGuestIds)
          ? parsed.mergedGuestIds.map(String)
          : [],
      };
    } catch (error) {
      console.error('Could not read the synchronized cart update.', error);
      return { items: [], deletedItems: {}, mergedGuestIds: [] };
    }
  }

  private getCartStorageKey(user: User | null): string {
    if (user?.id !== undefined && user.id !== null) {
      return `cart:user:${user.id}`;
    }

    if (user?.email) {
      return `cart:user:${encodeURIComponent(user.email.trim().toLowerCase())}`;
    }

    return this.guestCartKey;
  }

  private switchCart(storageKey: string): void {
    if (storageKey === this.cartStorageKey) return;

    const previousStorageKey = this.cartStorageKey;
    this.cartStorageKey = storageKey;
    const rawStoredCart = getLocalStorageItem(storageKey);
    const storedCartData = this.readStoredCart(storageKey);
    const invalidStoredShape = this.hasInvalidStoredCartShape(rawStoredCart);
    const { cart: storedItems } = this.ensureUniqueCartItemIds(storedCartData.items);
    const storedCart = { ...storedCartData, items: storedItems };
    this.deletedItemTimestamps = new Map(Object.entries(storedCart.deletedItems));
    this.mergedGuestIds = new Set(storedCart.mergedGuestIds);
    const shouldMergeGuestCart =
      previousStorageKey === this.guestCartKey && storageKey !== this.guestCartKey;
    const guestCartData = shouldMergeGuestCart
      ? this.readStoredCart(this.guestCartKey)
      : { items: [], deletedItems: {}, mergedGuestIds: [] };
    const { cart: guestItems } = this.ensureUniqueCartItemIds(guestCartData.items);
    const guestCart = { ...guestCartData, items: guestItems };
    if (shouldMergeGuestCart) {
      this.cartSubject.next([]);
      let mergeSucceeded = true;
      for (const item of storedCart.items) {
        mergeSucceeded = this.addToCart(item) && mergeSucceeded;
      }
      for (const item of guestCart.items) {
        const guestId = String(item?.id ?? '');
        if (!guestId || this.mergedGuestIds.has(guestId)) continue;
        this.mergedGuestIds.add(guestId);
        const added = this.addToCart(item);
        if (!added) {
          this.mergedGuestIds.delete(guestId);
          mergeSucceeded = false;
        } else {
          mergeSucceeded = mergeSucceeded && added;
        }
      }
      if (mergeSucceeded && this.updateCartStorage(this.getCart())) {
        try {
          removeLocalStorageItem(this.guestCartKey);
        } catch (error) {
          console.error('Could not remove the merged guest cart from browser storage.', error);
          this.cartNoticeSubject.next(
            'Koszyki zostały połączone, ale nie udało się usunąć koszyka gościa z tego urządzenia.',
          );
        }
      } else {
        this.cartNoticeSubject.next(
          'Koszyk gościa jest nadal zapisany, ponieważ nie udało się w pełni zapisać koszyka konta.',
        );
      }
      return;
    }

    const { cart, changed } = this.ensureUniqueCartItemIds(storedCart.items);
    this.cartSubject.next(cart);
    if (changed || invalidStoredShape) {
      this.updateCartStorage(cart);
    }
  }

  getCart(): any[] {
    return this.copyCartItems(this.cartSubject.getValue());
  }

  addToCart(item: any): boolean {
    if (!item || typeof item !== 'object' || !item.product) {
      this.cartNoticeSubject.next('Nie udało się dodać tego produktu do koszyka.');
      return false;
    }
    const cart = this.getCart();
    const newItem = this.cloneValue(item);
    const minimum = getCartItemMinimumQuantity(newItem);
    if (
      newItem.quantity === undefined ||
      newItem.quantity === null ||
      newItem.quantity === ''
    ) {
      newItem.quantity = minimum;
    } else {
      newItem.quantity = Number(newItem.quantity);
    }

    const existingIds = new Set(cart.map((cartItem: any) => String(cartItem?.id)));
    const itemId = newItem?.id === undefined || newItem?.id === null ? '' : String(newItem.id);
    newItem.id = !itemId || existingIds.has(itemId) ? this.createCartItemId(existingIds) : itemId;

    // Enhanced logic to check for existing items considering variants and colors
    const existingItemIndex = cart.findIndex((cartItem: any) => {
      // Same product check
      if (String(cartItem?.product?.id) !== String(newItem?.product?.id)) {
        return false;
      }

      // Check combination match
      if (cartItem?.combination?.id !== newItem?.combination?.id) {
        return false;
      }

      // Check selected color match
      if (cartItem?.selectedColor?.id !== newItem?.selectedColor?.id) {
        return false;
      }

      // Check selected downloads match
      const cartDownloads = cartItem?.selectedDownloads || [];
      const itemDownloads = newItem?.selectedDownloads || [];

      if (cartDownloads.length !== itemDownloads.length) {
        return false;
      }

      const cartDownloadIds = cartDownloads
        .map((download: any) => String(download?.id))
        .sort();
      const itemDownloadIds = itemDownloads
        .map((download: any) => String(download?.id))
        .sort();
      if (cartDownloadIds.some((id: string, index: number) => id !== itemDownloadIds[index])) {
        return false;
      }

      // Enhanced variant comparison
      const cartVariants = cartItem?.variants || {};
      const itemVariants = newItem?.variants || {};

      const cartVariantKeys = Object.keys(cartVariants);
      const itemVariantKeys = Object.keys(itemVariants);

      // If different number of variants, they're different items
      if (cartVariantKeys.length !== itemVariantKeys.length) {
        return false;
      }

      // If no variants for both, they're the same (assuming same color already checked)
      if (cartVariantKeys.length === 0 && itemVariantKeys.length === 0) {
        return true;
      }

      // Compare each variant thoroughly
      for (const variantKey of cartVariantKeys) {
        const cartVariant = cartVariants[variantKey];
        const itemVariant = itemVariants[variantKey];

        // If item doesn't have this variant key, they're different
        if (!itemVariant) {
          return false;
        }

        // Compare variant IDs (primary comparison)
        if (cartVariant?.id !== itemVariant?.id) {
          return false;
        }

        // Also compare variant values as backup
        if (cartVariant?.value !== itemVariant?.value) {
          return false;
        }

        // Compare variant names/labels if backup
        if (cartVariant?.variant?.id !== itemVariant?.variant?.id) {
          return false;
        }
      }

      return true;
    });

    if (existingItemIndex !== -1) {
      const existingItem = cart[existingItemIndex];
      const quantity =
        Number(existingItem?.quantity || 0) + Number(newItem?.quantity || 0);
      const mergedItem = {
        ...existingItem,
        ...newItem,
        id: existingItem.id,
        quantity,
      };
      const quantityError = getCartItemQuantityError(mergedItem);
      if (quantityError) {
        this.cartNoticeSubject.next(
          `${newItem?.product?.label || newItem?.product?.name || 'Produkt'}: ${quantityError}`,
        );
        return false;
      }
      cart[existingItemIndex] = mergedItem;
    } else {
      if (cart.length >= MAX_CART_LINE_ITEMS) {
        this.cartNoticeSubject.next(
          `Koszyk może zawierać maksymalnie ${MAX_CART_LINE_ITEMS} pozycji.`,
        );
        return false;
      }
      const requestedQuantity = Number(newItem.quantity);
      if (requestedQuantity < minimum) newItem.quantity = minimum;
      const quantityError = getCartItemQuantityError(newItem);
      if (quantityError) {
        this.cartNoticeSubject.next(
          `${newItem?.product?.label || newItem?.product?.name || 'Produkt'}: ${quantityError}`,
        );
        return false;
      }
      cart.push(newItem);
    }

    return this.updateCartStorage(cart);
  }

  removeFromCart(itemId: string | number): void {
    const cart = this.getCart().filter((item: any) => String(item?.id) !== String(itemId));
    this.updateCartStorage(cart);
  }

  updateProductPricing(updatedProducts: any[]): void {
    if (!Array.isArray(updatedProducts) || !updatedProducts.length) return;

    const updatedById = new Map(
      updatedProducts.map((product) => [String(product?.id), product]),
    );
    const updatedCart = this.getCart().map((item) => {
      const currentProduct = item?.product;
      const updatedProduct = updatedById.get(String(currentProduct?.id));
      if (!currentProduct || !updatedProduct) return item;

      const volumeDiscounts = Array.isArray(updatedProduct.volumeDiscounts)
        ? updatedProduct.volumeDiscounts
        : [];
      const quantity = Number(item?.quantity) || 0;
      const applicableVolumeDiscount = volumeDiscounts
        .filter((discount: any) => Number(discount?.minQuantity) <= quantity)
        .sort(
          (left: any, right: any) =>
            Number(left.minQuantity) - Number(right.minQuantity),
        )
        .pop() || null;
      const currentDownloads = new Map(
        (Array.isArray(updatedProduct.selectedDownloads)
          ? updatedProduct.selectedDownloads
          : []
        ).map((download: any) => [String(download?.id), download]),
      );

      return {
        ...item,
        price: updatedProduct.priceHT,
        priceHT: updatedProduct.priceHT,
        originalPriceHT: updatedProduct.priceHT,
        appliedVolumeDiscount: applicableVolumeDiscount,
        selectedDownloads: (item?.selectedDownloads || []).flatMap((download: any) => {
          const currentDownload = currentDownloads.get(String(download?.id));
          return currentDownload ? [{ ...download, ...currentDownload }] : [];
        }),
        product: {
          ...currentProduct,
          priceHT: updatedProduct.priceHT,
          discountRate: updatedProduct.discountRate,
          tvaRate: updatedProduct.tvaRate,
          volumeDiscounts,
        },
      };
    });
    this.updateCartStorage(updatedCart);
  }

  updateProductStock(updatedProducts: any[]): void {
    if (!Array.isArray(updatedProducts) || !updatedProducts.length) return;

    const updatedById = new Map(
      updatedProducts.map((product) => [String(product?.id), product]),
    );
    const updatedCart = this.getCart().map((item) => {
      const currentProduct = item?.product;
      const updatedProduct = updatedById.get(String(currentProduct?.id));
      if (!currentProduct || !updatedProduct) return item;

      const product = {
        ...currentProduct,
        quantity: updatedProduct.quantity,
        availability: updatedProduct.availability,
      };
      const minimumQuantity = Number(updatedProduct.minimalQuantity);
      if (Number.isInteger(minimumQuantity) && minimumQuantity > 0) {
        product.unit_sales = {
          ...(currentProduct.unit_sales || {}),
          minimal_quantity: minimumQuantity,
        };
      }

      return { ...item, product };
    });
    this.updateCartStorage(updatedCart);
  }

  clearCart(): void {
    this.updateCartStorage([]);
  }

  getCartCount(): number {
    return this.getCart().reduce(
      (total: number, item: any) => total + (Number(item.quantity) || 0),
      0
    );
  }

  updateCart(itemId: string | number, quantity: number): boolean {
    if (!Number.isInteger(quantity) || quantity <= 0 || quantity > MAX_CART_ITEM_QUANTITY) {
      return false;
    }
    const cart = this.cartSubject.getValue();
    const itemIndex = cart.findIndex((item: any) => String(item?.id) === String(itemId));
    return this.updateCartAtIndex(itemIndex, quantity);
  }

  updateCartAtIndex(itemIndex: number, quantity: number): boolean {
    if (
      !Number.isInteger(itemIndex) ||
      itemIndex < 0 ||
      !Number.isInteger(quantity) ||
      quantity <= 0 ||
      quantity > MAX_CART_ITEM_QUANTITY
    ) {
      return false;
    }
    const cart = this.cartSubject.getValue();
    if (itemIndex >= cart.length) return false;
    const quantityError = getCartItemQuantityError({
      ...cart[itemIndex],
      quantity,
    });
    const correctingStockViolation =
      quantity < Number(cart[itemIndex]?.quantity) &&
      quantityError?.startsWith('Only ');
    if (quantityError && !correctingStockViolation) return false;

    const updatedCart = cart.map((item: any, index: number) =>
      index === itemIndex ? { ...item, quantity } : item,
    );
    return this.updateCartStorage(updatedCart);
  }

  restoreCartItem(item: any, index: number): void {
    if (!item?.id || this.cartSubject.getValue().some(cartItem => String(cartItem.id) === String(item.id))) {
      return;
    }

    const cart = this.getCart();
    if (cart.length >= MAX_CART_LINE_ITEMS) {
      this.cartNoticeSubject.next(
        `Koszyk może zawierać maksymalnie ${MAX_CART_LINE_ITEMS} pozycji.`,
      );
      return;
    }
    cart.splice(Math.max(0, Math.min(index, cart.length)), 0, item);
    this.updateCartStorage(cart);
  }

  private ensureUniqueCartItemIds(cart: any[]): { cart: any[]; changed: boolean } {
    if (!Array.isArray(cart)) {
      console.error('Saved cart data is not a list. Resetting the invalid cart.');
      return { cart: [], changed: true };
    }

    const usedIds = new Set<string>();
    let changed = false;
    const normalizedCart = cart.slice(0, MAX_CART_LINE_ITEMS).map((item: any) => {
      const currentId = item?.id === undefined || item?.id === null ? '' : String(item.id);
      const id = !currentId || usedIds.has(currentId)
        ? this.createCartItemId(usedIds)
        : currentId;
      usedIds.add(id);

      const normalizedQuantity =
        typeof item?.quantity === 'string' && Number.isInteger(Number(item.quantity))
          ? Number(item.quantity)
          : item?.quantity;
      if (item?.id === id && normalizedQuantity === item?.quantity) return item;
      changed = true;
      return { ...item, id, quantity: normalizedQuantity };
    });
    if (cart.length > MAX_CART_LINE_ITEMS) changed = true;

    return { cart: normalizedCart, changed };
  }

  private copyCartItems(cart: any[]): any[] {
    return this.cloneValue(cart);
  }

  private cloneValue<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map((entry) => this.cloneValue(entry)) as T;
    }
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .map(([key, entry]) => [key, this.cloneValue(entry)]),
      ) as T;
    }
    return value;
  }

  private createCartItemId(existingIds: Set<string>): string {
    let id: string;
    do {
      id = `cart-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    } while (existingIds.has(id));
    return id;
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.storageListener);
    }
    this.cartNoticeSubject.complete();
  }
}
